import { create } from "zustand";
import type { UIMessage } from "ai";
import type { ChatSession } from "../types";
import { caiApi } from "../api";

const MAX_SESSIONS_PER_DOCUMENT = 50;

interface SessionState {
  sessions: ChatSession[];
  activeSessionId: string | null;

  /** Fetch existing sessions from the backend and populate the store. */
  loadSessions: (sessionTypes: string[]) => Promise<void>;
  /** Create a session via CAi backend. Falls back to local-only if API is not initialized. */
  createSession: (sessionType: string, documentId?: string) => Promise<ChatSession>;
  switchSession: (sessionId: string) => Promise<void>;
  /** Delete session. Calls CAi backend for logical delete if initialized. */
  deleteSession: (sessionId: string) => void;
  updateSessionMessages: (
    sessionId: string,
    messages: ChatSession["messages"]
  ) => void;
  /** Load messages for a session from DynamoDB. */
  loadSessionMessages: (sessionId: string) => Promise<UIMessage[]>;
  getSessionsForDocument: (documentId: string) => ChatSession[];
}

/**
 * Tracks how many messages have already been persisted per session,
 * so we only send the delta on each save.
 */
const persistedCount = new Map<string, number>();

/** Save only new (unsaved) messages to DynamoDB (fire-and-forget). */
function persistMessages(sessionId: string, messages: UIMessage[]): void {
  if (!caiApi.isInitialized) return;

  const alreadySaved = persistedCount.get(sessionId) ?? 0;
  const newMessages = messages.slice(alreadySaved);
  if (newMessages.length === 0) return;

  caiApi.persistMessages(sessionId, newMessages).catch((err) => {
    console.warn("Failed to persist plugin messages:", err);
  });

  persistedCount.set(sessionId, messages.length);
}

export const useSessionStore = create<SessionState>()(
  (set, get) => ({
    sessions: [],
    activeSessionId: null,

    loadSessions: async (sessionTypes) => {
      if (!caiApi.isInitialized) return;
      try {
        const backendSessions = await caiApi.getAllSessions(sessionTypes);
        const existing = new Set(get().sessions.map((s) => s.id));
        const newSessions: ChatSession[] = backendSessions
          .filter((bs) => !existing.has(bs.session_id))
          .map((bs) => ({
            id: bs.session_id,
            name: bs.session_name ?? `Chat`,
            documentId: bs.session_type ?? "default",
            messages: [],
            createdAt: bs.creation_time ? new Date(bs.creation_time).getTime() : Date.now(),
            updatedAt: bs.creation_time ? new Date(bs.creation_time).getTime() : Date.now(),
          }));

        if (newSessions.length > 0) {
          set((state) => ({
            sessions: [...newSessions, ...state.sessions],
          }));
        }
      } catch (err) {
        console.warn("Failed to load sessions from backend:", err);
      }
    },

    createSession: async (sessionType, documentId) => {
      let backendSessionId: string | null = null;
      let backendName: string | null = null;

      // Create session on the backend (required — errors propagate to caller)
      if (caiApi.isInitialized) {
        const caiSession = await caiApi.createSession(sessionType);
        backendSessionId = caiSession.session_id;
        backendName = caiSession.session_name;
      }

      const session: ChatSession = {
        id: backendSessionId ?? crypto.randomUUID(),
        name: backendName ?? `Chat ${new Date().toLocaleString()}`,
        documentId: documentId ?? sessionType,
        messages: [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      set((state) => {
        const docSessions = state.sessions.filter(
          (s) => s.documentId === session.documentId
        );

        let sessions = [...state.sessions, session];

        // Prune oldest if over limit
        if (docSessions.length >= MAX_SESSIONS_PER_DOCUMENT) {
          const oldest = docSessions
            .sort((a, b) => a.updatedAt - b.updatedAt)
            .slice(0, docSessions.length - MAX_SESSIONS_PER_DOCUMENT + 1);
          const oldestIds = new Set(oldest.map((s) => s.id));
          sessions = sessions.filter((s) => !oldestIds.has(s.id));
        }

        return { sessions, activeSessionId: session.id };
      });

      return session;
    },

    switchSession: async (sessionId) => {
      set({ activeSessionId: sessionId });

      // Hydrate messages from DynamoDB if the session has no messages loaded
      const session = get().sessions.find((s) => s.id === sessionId);
      if (session && session.messages.length === 0 && caiApi.isInitialized) {
        try {
          const messages = await caiApi.loadMessages(sessionId) as UIMessage[];
          if (messages.length > 0) {
            persistedCount.set(sessionId, messages.length);
            set((state) => ({
              sessions: state.sessions.map((s) =>
                s.id === sessionId
                  ? { ...s, messages, updatedAt: Date.now() }
                  : s
              ),
            }));
          }
        } catch (err) {
          console.warn("Failed to load plugin messages:", err);
        }
      }
    },

    deleteSession: (sessionId) => {
      persistedCount.delete(sessionId);

      // Logical delete on backend (fire-and-forget)
      if (caiApi.isInitialized) {
        caiApi.deleteSession(sessionId).catch((err) => {
          console.warn("Failed to delete backend session:", err);
        });
      }

      set((state) => {
        const sessions = state.sessions.filter((s) => s.id !== sessionId);
        const activeSessionId =
          state.activeSessionId === sessionId
            ? sessions[sessions.length - 1]?.id ?? null
            : state.activeSessionId;
        return { sessions, activeSessionId };
      });
    },

    updateSessionMessages: (sessionId, messages) => {
      set((state) => ({
        sessions: state.sessions.map((s) =>
          s.id === sessionId
            ? { ...s, messages, updatedAt: Date.now() }
            : s
        ),
      }));

      // Persist to DynamoDB
      persistMessages(sessionId, messages);
    },

    loadSessionMessages: async (sessionId) => {
      if (!caiApi.isInitialized) return [];
      try {
        const messages = await caiApi.loadMessages(sessionId) as UIMessage[];
        if (messages.length > 0) {
          set((state) => ({
            sessions: state.sessions.map((s) =>
              s.id === sessionId
                ? { ...s, messages, updatedAt: Date.now() }
                : s
            ),
          }));
        }
        return messages;
      } catch (err) {
        console.warn("Failed to load plugin messages:", err);
        return [];
      }
    },

    getSessionsForDocument: (documentId) =>
      get().sessions.filter((s) => s.documentId === documentId),
  })
);
